export function Governance() {
  return (
    <>
      <h1>Governance</h1>

      <p>
        Most pre-seed companies have a README and a prayer. TAIGA has a
        STRIDE threat model with 22 identified threats, a GDPR Data
        Protection Impact Assessment, 16 formal security policies, and an
        AI Agent-driven ISO 27001-compliant ISMS — implemented before
        closing the pre-seed round.
      </p>
      <p>
        This is not compliance theater. It is competitive advantage. Every
        piece of security infrastructure TAIGA builds for itself becomes a
        feature it sells to customers. We eat our own dogfood. The same
        governance engine that enforces policy on TAIGA's internal agents
        enforces policy on customer agents. Security is not a cost center —
        it is the product.
      </p>

      <hr />

      <h2>Why This Matters for Investors</h2>
      <p>
        In enterprise sales, security documentation is the difference
        between a 2-week close and a 6-month close. Every regulated
        enterprise runs a vendor security assessment before signing. Most
        startups fail it. TAIGA passes it — today, at pre-seed.
      </p>
      <p>
        The founding team built and maintained ISO 27001 at Cloud2. They
        have been through the audits, the customer security questionnaires,
        the procurement gauntlets. They know exactly what enterprises ask
        for, because they have answered those questions for years. TAIGA's
        security posture removes the single biggest objection regulated
        enterprises have: "Can we trust this vendor?"
      </p>
      <p>
        This is a moat. Competitors who skip security early will spend 6-12
        months catching up when their first enterprise customer asks for a
        SOC 2 report. TAIGA already has the foundation in place.
      </p>

      <hr />

      <h2>What's Already Implemented</h2>
      <p>
        The following artifacts are complete — not planned, not in
        progress, done:
      </p>

      <table>
        <thead>
          <tr>
            <th>Artifact</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Threat Model</td>
            <td>STRIDE methodology, 22 threats identified and mitigated</td>
          </tr>
          <tr>
            <td>DPIA</td>
            <td>
              GDPR Article 35, full Data Protection Impact Assessment
            </td>
          </tr>
          <tr>
            <td>Data Flow Diagram</td>
            <td>
              All data flows mapped, including third-party processors
            </td>
          </tr>
          <tr>
            <td>Risk Register</td>
            <td>
              7 platform risks, business risks, regulatory risks — all rated
            </td>
          </tr>
          <tr>
            <td>Security Policies</td>
            <td>16 formal policies covering the full security domain</td>
          </tr>
          <tr>
            <td>Incident Response Plan</td>
            <td>
              Documented procedures, escalation paths, notification
              timelines
            </td>
          </tr>
          <tr>
            <td>Architecture Security</td>
            <td>6 trust boundaries identified and hardened</td>
          </tr>
          <tr>
            <td>AI Agent-driven ISMS</td>
            <td>
              ISO 27001 compliant, first version for development and
              operations
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Security Policies Summary</h3>
      <ul>
        <li>
          <strong>SEC-1 (Authentication &amp; Access Control):</strong>{" "}
          OAuth via approved providers, short-lived credentials, no shared
          accounts, MFA required for admin access.
        </li>
        <li>
          <strong>SEC-2 (Data Protection):</strong> Encryption at rest
          (KMS), TLS 1.2+ in transit, no PII in logs, tenant data isolated
          via Row-Level Security.
        </li>
        <li>
          <strong>SEC-3 (Network Security):</strong> Backend not
          internet-accessible, WAF on all public endpoints, database in
          private subnet.
        </li>
        <li>
          <strong>SEC-4 (Secrets Management):</strong> No secrets in
          version control, AWS Secrets Manager for all credentials, annual
          rotation policy.
        </li>
      </ul>

      <hr />

      <h2>Certification Roadmap</h2>
      <table>
        <thead>
          <tr>
            <th>Milestone</th>
            <th>Timeline</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>AI Agent-driven ISMS (ISO 27001 compliant)</td>
            <td>Pre-seed</td>
            <td>
              First version implemented for development and operations
            </td>
          </tr>
          <tr>
            <td>ISO 27001 certification</td>
            <td>Early — within months</td>
            <td>
              Considering early certification based on ISMS maturity
            </td>
          </tr>
          <tr>
            <td>SOC 2 Type I</td>
            <td>Post-seed</td>
            <td>Planned</td>
          </tr>
        </tbody>
      </table>

      <p>
        The founding team led ISO 27001 certification at Cloud2, maintained
        it over multiple years, and passed numerous client security
        assessments. TAIGA has already implemented a first version of an AI
        Agent-driven ISO 27001-compliant ISMS for development and
        operations. Given the maturity of the system, early certification
        is under consideration — within months, not years.
      </p>

      <hr />

      <h2>Data Protection</h2>
      <table>
        <thead>
          <tr>
            <th>Measure</th>
            <th>Implementation</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Encryption at rest</td>
            <td>AWS KMS for RDS, S3 (SSE-S3)</td>
          </tr>
          <tr>
            <td>Encryption in transit</td>
            <td>TLS 1.2+ everywhere</td>
          </tr>
          <tr>
            <td>Multi-tenant isolation</td>
            <td>PostgreSQL Row-Level Security</td>
          </tr>
          <tr>
            <td>Data classification</td>
            <td>4 levels: Public, Confidential, Personal (PII), Secret</td>
          </tr>
          <tr>
            <td>Data minimization</td>
            <td>Only necessary context sent to LLMs</td>
          </tr>
          <tr>
            <td>Retention policies</td>
            <td>7 days to 2 years depending on data type</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>AI-Specific Security</h2>
      <p>
        Every AI governance control TAIGA enforces on its own agents is a
        control it can enforce for customers. This is the dogfooding
        advantage — TAIGA does not sell theoretical security, it sells
        battle-tested security.
      </p>

      <ul>
        <li>
          <strong>Human-in-the-loop:</strong> All AI suggestions require
          human approval before taking effect.
        </li>
        <li>
          <strong>Prompt injection defense:</strong> Input sanitization,
          output validation, and policy checks at every stage.
        </li>
        <li>
          <strong>Data minimization to LLM:</strong> Only the minimum
          necessary context is sent to language model providers.
        </li>
        <li>
          <strong>No training on customer data:</strong> Contractual
          agreements (DPAs) with all LLM providers prohibit training on
          customer data.
        </li>
        <li>
          <strong>Bedrock Guardrails:</strong> AWS Bedrock Guardrails
          enforce content filtering and PII detection.
        </li>
        <li>
          <strong>Policy engine validates AI output:</strong> All
          AI-generated output passes through a policy engine before
          delivery to users.
        </li>
      </ul>

      <hr />

      <h2>EU Data Sovereignty</h2>
      <p>
        EU data residency is enforced at the infrastructure level, not the
        policy level. Your data cannot leave the EU — it is not a setting,
        it is architecture.
      </p>
      <p>
        All infrastructure runs in <strong>eu-central-1 (Frankfurt)</strong>.
        Data processing, storage, and AI inference all occur within the EU.
        There is no US failover, no cross-region replication outside the EU,
        no "oops" path for data to end up in a non-EU jurisdiction.
      </p>

      <hr />

      <h2>Risk Transparency</h2>
      <p>
        We document risks openly. Hiding risks from investors is a red
        flag. Documenting them with mitigations is a sign of maturity. Here
        is what we track.
      </p>

      <h3>STRIDE Threat Model</h3>
      <p>
        22 threats identified across 6 STRIDE categories, all with defined
        mitigations:
      </p>

      <p>
        <strong>Spoofing (4 threats):</strong> Stolen OAuth tokens, fake
        OAuth callback, service-to-service spoofing, man-in-the-middle on
        LLM calls. Mitigated by short-lived JWTs, OAuth state validation,
        IAM roles, and TLS.
      </p>

      <p>
        <strong>Tampering (5 threats):</strong> Request tampering, direct
        database modification, S3 artifact tampering, audit log
        modification, prompt injection. Mitigated by TLS, private subnets,
        S3 versioning, append-only audit logs, and input/output
        validation.
      </p>

      <p>
        <strong>Repudiation (3 threats):</strong> User denies action,
        conversation content disputes, specification changes. Mitigated by
        immutable audit log, transcript retention, and version history.
      </p>

      <p>
        <strong>Information Disclosure (7 threats):</strong> Multi-tenant
        data leak (Critical), sensitive error messages, PII in logs, data
        exposure to LLM provider, public bucket exposure (Critical),
        credential leak (Critical), browser storage exposure. Mitigated by
        RLS enforcement, generic error messages, log sanitization, data
        minimization, private-only S3 buckets, Secrets Manager, and
        httpOnly cookies.
      </p>

      <p>
        <strong>Denial of Service (5 threats):</strong> DDoS,
        application-layer DoS, LLM cost exhaustion, query exhaustion, large
        payload attacks. Mitigated by WAF, rate limiting, usage caps,
        connection pooling, and input size limits.
      </p>

      <p>
        <strong>Elevation of Privilege (5 threats):</strong> Role
        escalation (Critical), IDOR attacks (Critical), capability bypass,
        LLM tricked into privileged actions, database role gains superuser
        (Critical). Mitigated by audit-logged role changes, RLS
        enforcement, deny-by-default permissions, human approval for LLM
        actions, and minimal database privileges.
      </p>

      <h3>High-Priority Threats</h3>
      <table>
        <thead>
          <tr>
            <th>Threat</th>
            <th>Severity</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Multi-tenant data leak</td>
            <td>Critical</td>
          </tr>
          <tr>
            <td>IDOR attacks</td>
            <td>Critical</td>
          </tr>
          <tr>
            <td>Prompt injection</td>
            <td>High</td>
          </tr>
          <tr>
            <td>LLM privilege escalation</td>
            <td>High</td>
          </tr>
          <tr>
            <td>Data exposure to LLM</td>
            <td>High</td>
          </tr>
          <tr>
            <td>LLM cost exhaustion</td>
            <td>Medium</td>
          </tr>
        </tbody>
      </table>

      <h3>Risk Register</h3>

      <h4>Platform Risks</h4>
      <table>
        <thead>
          <tr>
            <th>Risk</th>
            <th>Likelihood</th>
            <th>Impact</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>LLM Output Quality</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Multi-Tenant Data Leak</td>
            <td>Low</td>
            <td>High</td>
          </tr>
          <tr>
            <td>LLM Provider Outage</td>
            <td>Medium</td>
            <td>High</td>
          </tr>
          <tr>
            <td>Scope Creep</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Early Customer Data</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Single Point of Failure</td>
            <td>Low</td>
            <td>High</td>
          </tr>
          <tr>
            <td>Cost Overrun (LLM)</td>
            <td>Medium</td>
            <td>Low</td>
          </tr>
        </tbody>
      </table>

      <h4>Business Risks</h4>
      <table>
        <thead>
          <tr>
            <th>Risk</th>
            <th>Likelihood</th>
            <th>Impact</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Well-funded competitor</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Slower AI adoption</td>
            <td>Low</td>
            <td>High</td>
          </tr>
          <tr>
            <td>Economic downturn</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Customer AI trust</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Pre-revenue burn</td>
            <td>Medium</td>
            <td>High</td>
          </tr>
          <tr>
            <td>Pricing model</td>
            <td>Low</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Customer concentration</td>
            <td>Medium</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Key founder departure</td>
            <td>Very Low</td>
            <td>Critical</td>
          </tr>
          <tr>
            <td>Technical debt</td>
            <td>Medium</td>
            <td>Low</td>
          </tr>
          <tr>
            <td>Scalability issues</td>
            <td>Low</td>
            <td>Medium</td>
          </tr>
        </tbody>
      </table>

      <h4>Regulatory Risks</h4>
      <table>
        <thead>
          <tr>
            <th>Risk</th>
            <th>Likelihood</th>
            <th>Impact</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>EU AI Act requirements change</td>
            <td>Low</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>Additional regulations</td>
            <td>Medium</td>
            <td>Low</td>
          </tr>
          <tr>
            <td>Data sovereignty tightens</td>
            <td>Medium</td>
            <td>Low (sovereign deployment planned)</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>GDPR Compliance</h2>
      <p>
        DPIA has been completed and data processing is fully documented.
        Legal basis is documented as contract performance and legitimate
        interest. Data subject rights are designed into the platform from
        the start — not retrofitted, built in. Breach notification
        procedures follow the 72-hour requirement. DPAs are in place with
        each LLM provider prohibiting training on customer data, all data
        processing occurs in European regions, the right to erasure is
        fully supported, and human gates on automated decisions satisfy
        GDPR Article 22.
      </p>

      <h3>Data Flows to Third Parties</h3>
      <table>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Data Shared</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Google</td>
            <td>OAuth credentials</td>
            <td>Authentication only</td>
          </tr>
          <tr>
            <td>Anthropic</td>
            <td>Chat context (no direct PII)</td>
            <td>No training on data, DPA in place</td>
          </tr>
          <tr>
            <td>OpenAI</td>
            <td>Council interactions</td>
            <td>No training on data, DPA in place</td>
          </tr>
          <tr>
            <td>AWS</td>
            <td>All data</td>
            <td>
              EU (Frankfurt) region, DPA in place, encryption at rest and
              in transit
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Data Subject Rights</h3>
      <table>
        <thead>
          <tr>
            <th>Right</th>
            <th>Response Time</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Access</td>
            <td>30 days</td>
          </tr>
          <tr>
            <td>Rectification</td>
            <td>Immediate</td>
          </tr>
          <tr>
            <td>Erasure</td>
            <td>30 days</td>
          </tr>
          <tr>
            <td>Restriction</td>
            <td>Immediate</td>
          </tr>
          <tr>
            <td>Portability (JSON export)</td>
            <td>30 days</td>
          </tr>
          <tr>
            <td>Object</td>
            <td>Immediate</td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
